import { useMediaQuery, useTheme } from '@mui/material';
import { getSelectorAttribute, usagePageSelectors } from '@vy/app-selectors';
import { isNotEmptyArray, withDefault } from '@vy/utils';
import React from 'react';
import { VyLoader } from '../../../../components/new-design/feedback/Loader/VyLoader';
import { VyMultipleAutoComplete } from '../../../../components/new-design/inputs/VyMultipleAutoComplete';
import { VyRangePicker } from '../../../../components/new-design/inputs/VyRangePicker';
import { VyTableToolbar } from '../../../../components/new-design/table/vy-table-toolbar/vy-table-toolbar';
import { withSuspenseBoundary } from '../../../../components/utility/SuspenseBoundary';
import { useAllCustomersNames } from '../../../../hooks/requests/use-customers-names';
import { useMeters } from '../../../../hooks/requests/use-meters/use-meters';
import { useStateParams } from '../../../../hooks/use-state-params';
import { deserializeStringArrayParam, serializeStringArrayParam } from '../../../../utils/params-formatting';
import { useEventNameMeters } from '../data-metering-hooks/use-event-name-meters';
import { DataMeteringMenu } from './DataMeteringMenu';
import { ExportToCsvButton } from './ExportToCsvButton';
import { ProductAutocomplete } from './ProductAutocomplete';

type DataMeteringToolbarContentProps = {
  selectedMeasurementIds: string[];
  totalMeasurementsCount: number;
  eventNames: string[];
  deleteVisible?: boolean;
};

const DataMeteringToolbarContent: React.FC<DataMeteringToolbarContentProps> = ({
  selectedMeasurementIds,
  totalMeasurementsCount,
  eventNames,
  deleteVisible = false,
}) => {
  const {
    data: { customers },
  } = useAllCustomersNames();
  const {
    data: { meters },
  } = useMeters();

  const filterMeters = useEventNameMeters(eventNames, meters);

  const [selectedCustomersIds, setCustomersIds] = useStateParams<string[]>(
    [],
    'customers',
    serializeStringArrayParam,
    deserializeStringArrayParam,
  );

  const [selectedMetersIds, setSelectedMetersIds] = useStateParams<string[]>(
    [],
    'meters',
    serializeStringArrayParam,
    deserializeStringArrayParam,
  );

  const vyTheme = useTheme();
  const isLargeScreen = useMediaQuery(vyTheme.breakpoints.up('lg'));

  const selectedCustomers = customers.filter((customer) =>
    withDefault(selectedCustomersIds, []).includes(customer.id),
  );

  return (
    <VyTableToolbar className="flex items-start z-50" sx={{ padding: '24px 16px' }}>
      <VyTableToolbar.Queries
        className="gap-3 overflow-hidden"
        sx={{
          alignItems: 'start',
          height: '120px',
          display: 'flex',
          flexWrap: 'nowrap',
        }}
      >
        <VyRangePicker
          textFieldProps={{
            sx: {
              width: isLargeScreen ? '200px' : '180px',
              flexShrink: 0,
              '& .MuiInputBase-root': {
                height: '40px',
                borderRadius: '8px',
              },
            },
          }}
        />

        <VyMultipleAutoComplete
          options={customers}
          value={selectedCustomers}
          onChange={(newSelectedCustomers) => {
            const newSelectedCustomersIds = newSelectedCustomers.flatMap((customer) => {
              if (
                !selectedCustomersIds?.includes(customer.id) &&
                isNotEmptyArray(customer.childCustomersIds)
              ) {
                return [customer.id, ...customer.childCustomersIds];
              }

              return [customer.id];
            });

            setCustomersIds(newSelectedCustomersIds);
          }}
          getOptionLabel={(customer) => customer.name}
          label="Customers"
          showSelectAll={true}
          size="small"
          sx={{
            width: '400px',
            minWidth: '400px',
            maxWidth: '400px',
            flexShrink: 0,
            '& .MuiInputBase-root': {
            borderRadius: '8px',
            },
          }}
          textFieldProps={{
            ...getSelectorAttribute(usagePageSelectors.header.customerDropdownButton),
          }}
        />

        <ProductAutocomplete
          filterMeters={filterMeters}
          selectedMetersIds={selectedMetersIds ?? []}
          setSelectedMetersIds={setSelectedMetersIds}
        />
      </VyTableToolbar.Queries>

      <VyTableToolbar.Actions>
        <div className="mt-6 flex gap-4">
          <ExportToCsvButton disabled={totalMeasurementsCount > 0} />

          <DataMeteringMenu {...{ selectedMeasurementIds, deleteVisible }} />
        </div>
      </VyTableToolbar.Actions>
    </VyTableToolbar>
  );
};

const DataMeteringToolbarContentWithSuspenseBoundary = withSuspenseBoundary(DataMeteringToolbarContent, {
  suspenseProps: {
    fallback: <VyLoader variant="rounded" height="100%" width="100%" />,
  },
});

export { DataMeteringToolbarContentWithSuspenseBoundary as DataMeteringToolbarContent };
